import * as React from 'react'
import moment from 'moment'
import FlexView from '@/modules/shared/components/FlexView'
import { useNavigate, useParams } from 'react-router-dom'

import { VehicleCalendarDayModel } from '@/app/models/vehicle/calendar/vehicleCalendarDayModel'
import { CalendarOption } from '@/app/constants/calendarOptions'
import { MergedActivitySource } from '@/app/constants/mergedActivitySource'
import { ActivitySource } from '@/app/constants/activitySource'
import { TmsType } from '@/app/constants/tmsType'
import { getActivitySource } from '@/app/services/activity/activitySourceResolver'
import { DATE_FORMAT } from '@/app/constants/date'

export enum CalendarMenuAction {
  GoToDayChart = 'GoToDayChart',
  GoToWeekChart = 'GoToWeekChart',
  ShowChart = 'ShowChart',
  ShowDetails = 'ShowDetails',
}

export const COLORS = {
  Gps: '#61A0DD',
  TmsLoading: '#FF6A00',
  TmsUnloading: '#0094FF',
  CardReading: '#6BBE4F',
  driverCardActivity: '#1EAF4A',
  tachographActivity: '#404040',
  noActivity: '#ED2229',
  analogTachographActivity: '#F7DC06',
  rest: '#C3DEE8',
  file: '#4945FF',
}

export type VehicleDayItemProps = {
  companyId: string
  translate: (text: string) => any
  day: VehicleCalendarDayModel
  height: number
  width: number
  selectedOptions?: Array<CalendarOption>
  onSelected: (day: VehicleCalendarDayModel, e: any) => void
  onMouseDown?: (day: VehicleCalendarDayModel, e: React.MouseEvent) => void
  onMouseEnterDay?: (day: VehicleCalendarDayModel) => void
  onContextMenuRequest?: (
    day: VehicleCalendarDayModel,
    anchor: { x: number; y: number },
  ) => void
  isContextMenuOpen?: boolean
  isSelected: boolean
  isMultiSelected?: boolean
  isFirstRow?: boolean
  isLastInRow?: boolean
}

const getActivityColor = (source: ActivitySource, defaultColor: string) => {
  switch (source) {
    case ActivitySource.DRIVER:
    case ActivitySource.DRIVER_1:
    case ActivitySource.DRIVER_2:
    case ActivitySource.TACHOGRAPH:
      return defaultColor
    case ActivitySource.USER:
    case ActivitySource.ANALOG:
      return COLORS.analogTachographActivity
    case ActivitySource.REST:
      return COLORS.rest
    case ActivitySource.FILE:
      return COLORS.file
    case ActivitySource.NO_DATA:
    case ActivitySource.EMPTY:
      return 'repeating-linear-gradient(45deg, #E4E4F7, #E4E4F7 4px, #F7F9FF 4px, #F7F9FF 8px)'
    default:
      return 'transparent'
  }
}

export const VehicleCalendarDayItem: React.FC<VehicleDayItemProps> = React.memo(
  (props) => {
    const {
      companyId,
      day,
      height,
      width,
      onSelected,
      onMouseDown,
      onMouseEnterDay,
      onContextMenuRequest,
      isContextMenuOpen,
      isSelected,
      isMultiSelected,
      isFirstRow = false,
      isLastInRow = false,
    } = props
    const navigate = useNavigate()
    const { vehicleId } = useParams<{ vehicleId?: string }>()
    const isWeekendOrHoliday = React.useMemo(() => {
      return (
        day != null &&
        (day.isHoliday || moment(day.activityDayLocalTimeDate).day() === 0)
      )
    }, [day])

    const isWeekend = React.useMemo(() => {
      return (
        day != null &&
        (moment(day.activityDayLocalTimeDate).day() === 0 ||
          moment(day.activityDayLocalTimeDate).day() === 6)
      )
    }, [day])

    const isFutureDay = React.useMemo(() => {
      const today = moment().startOf('day')
      const dayDate = moment(day.activityDayLocalTimeDate).startOf('day')
      return dayDate.isSameOrAfter(today)
    }, [day.activityDayLocalTimeDate])

    const isToday = React.useMemo(() => {
      const today = moment().startOf('day')
      const dayDate = moment(day.activityDayLocalTimeDate).startOf('day')
      return dayDate.isSame(today)
    }, [day.activityDayLocalTimeDate])

    const cellColor = React.useMemo(() => {
      return isWeekend ? '#F1F4FF' : '#FFFFFF'
    }, [isWeekend])

    const shouldShowActivities = React.useMemo(() => {
      const today = moment().startOf('day')
      const dayDate = moment(day.activityDayLocalTimeDate).startOf('day')
      return dayDate.isBefore(today)
    }, [day.activityDayLocalTimeDate])

    const isNoDataSource = (source: any) => {
      const resolved = getActivitySource(source)
      return (
        resolved === ActivitySource.NO_DATA || resolved === ActivitySource.EMPTY
      )
    }

    const isAllNoData = React.useMemo(() => {
      const card1 = day.driverCard1Acitivities || []
      const card2 = day.driverCard2Acitivities || []
      const tacho = day.tachographAcitivities || []
      if (card1.length === 0 && card2.length === 0 && tacho.length === 0)
        return false
      return (
        card1.every((v) => isNoDataSource(v.source)) &&
        card2.every((v) => isNoDataSource(v.source)) &&
        tacho.every((v) => isNoDataSource(v.source))
      )
    }, [day])

    const shouldShowBD = React.useMemo(() => {
      if (!isAllNoData) return false
      const today = moment().startOf('day')
      const dayDate = moment(day.activityDayLocalTimeDate).startOf('day')
      return dayDate.isBefore(today)
    }, [isAllNoData, day.activityDayLocalTimeDate])

    const isEmpty = day.dayNumber === 0

    return (
      <FlexView
        height={height}
        column
        className={isEmpty ? 'day-item-empty' : 'day-item'}
        hAlignContent="center"
        vAlignContent="top"
        style={{
          width: width,
          borderStyle: 'solid',
          borderLeftWidth: isEmpty ? 0 : 1,
          borderTopWidth: isFirstRow ? 1 : 0,
          borderBottomWidth: 1,
          borderRightWidth: isLastInRow ? 1 : isEmpty ? 0 : 1,
          borderColor: 'var(--color-border-base-300, #e5e7eb)',
          background: isEmpty ? '#fff' : cellColor,
          cursor: isEmpty ? 'default' : 'pointer',
          position: 'relative',
          overflow: 'hidden',
        }}
        onMouseMove={
          isEmpty
            ? undefined
            : (e) => {
                if (e.buttons) document.getSelection().removeAllRanges()
              }
        }
        onContextMenu={
          isEmpty
            ? undefined
            : (e) => {
                e.preventDefault()
                e.stopPropagation()
                onContextMenuRequest?.(day, { x: e.clientX, y: e.clientY })
              }
        }
        onMouseDown={
          isEmpty
            ? undefined
            : (e) => {
                onMouseDown?.(day, e)
              }
        }
        onMouseEnter={
          isEmpty
            ? undefined
            : () => {
                onMouseEnterDay?.(day)
              }
        }
        onClick={
          isEmpty
            ? undefined
            : (e) => {
                onSelected?.(day, e)
                document.getSelection().removeAllRanges()
              }
        }
        onDoubleClick={
          isEmpty
            ? undefined
            : () => {
                if (!vehicleId) return
                const date = moment(day.activityDayLocalTimeDate).format(
                  DATE_FORMAT,
                )
                navigate(
                  `/et/company/${companyId}/vehicleModule/vehicle/${vehicleId}/vehicleWeeklyActivity/date/${date}`,
                )
              }
        }
      >
        {!isEmpty && (isSelected || isMultiSelected) && (
          <div className="tw-absolute tw-inset-0 tw-z-10 tw-rounded-sm tw-bg-[#0043FF]/15" />
        )}
        <div className="tw-relative tw-flex tw-w-full tw-flex-1 tw-flex-col tw-justify-between">
          <div
            className={`tw-flex tw-w-full tw-justify-between tw-px-0.5 tw-pb-[3px] tw-pt-1.5 ${isToday ? 'tw-rounded tw-bg-[#2c57ff]' : ''}`}
          >
            <span
              className={`tw-text-xs tw-font-medium ${isToday ? 'tw-text-white' : isWeekendOrHoliday ? 'tw-text-red-500' : 'tw-text-content-base'}`}
            >
              {day.dayNumber !== 0 ? day.dayNumber : ''}
            </span>
          </div>
          <div className="tw-flex tw-items-center tw-gap-[1px] tw-px-[1px] tw-py-0.5">
            {[
              { condition: day.hasGpsData, color: COLORS.Gps },
              {
                condition:
                  day.hasTmsData &&
                  (day.tmsType === TmsType.Both ||
                    day.tmsType === TmsType.Loading),
                color: COLORS.TmsLoading,
              },
              {
                condition:
                  day.hasTmsData &&
                  (day.tmsType === TmsType.Both ||
                    day.tmsType === TmsType.Unloading),
                color: COLORS.TmsUnloading,
              },
            ].map(({ condition, color }, idx) =>
              condition ? (
                <div
                  key={idx}
                  style={{
                    height: 6,
                    width: 6,
                    background: color,
                    borderRadius: 3,
                  }}
                />
              ) : (
                <div key={idx} style={{ height: 6, width: 6 }} />
              ),
            )}
          </div>
          <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
            {shouldShowBD ? (
              <div
                className="tw-flex tw-flex-1 tw-items-end tw-justify-start tw-text-center"
                style={{
                  background:
                    'repeating-linear-gradient(45deg, #E4E4F7, #E4E4F7 4px, #F7F9FF 4px, #F7F9FF 8px)',
                }}
              >
                <span className="tw-text-xs tw-font-semibold tw-text-[#6B77A0]">
                  BD
                </span>
              </div>
            ) : shouldShowActivities ? (
              <div className="tw-flex tw-flex-1 tw-flex-col tw-justify-end tw-gap-1 tw-pb-1">
                <FlexView width="100%">
                  {(day.driverCard1Acitivities || []).map(
                    (value: MergedActivitySource, index: number) => (
                      <span
                        key={index}
                        style={{
                          height: 5,
                          flex: value.count,
                          background: getActivityColor(
                            getActivitySource(value.source),
                            COLORS.driverCardActivity,
                          ),
                        }}
                      />
                    ),
                  )}
                </FlexView>
                <FlexView width="100%">
                  {(day.driverCard2Acitivities || []).map(
                    (value: MergedActivitySource, index: number) => (
                      <span
                        key={index}
                        style={{
                          height: 5,
                          flex: value.count,
                          background: getActivityColor(
                            getActivitySource(value.source),
                            COLORS.driverCardActivity,
                          ),
                        }}
                      />
                    ),
                  )}
                </FlexView>
                <FlexView width="100%">
                  {(day.tachographAcitivities || []).map(
                    (value: MergedActivitySource, index: number) => (
                      <span
                        key={index}
                        style={{
                          height: 5,
                          flex: value.count,
                          background: getActivityColor(
                            getActivitySource(value.source),
                            COLORS.tachographActivity,
                          ),
                        }}
                      />
                    ),
                  )}
                </FlexView>
              </div>
            ) : null}
          </div>
        </div>
        {isToday && (
          <div className="tw-absolute tw-bottom-0 tw-right-4 tw-h-full tw-w-0.5 tw-bg-[#2c57ff]" />
        )}
        {day.driverCardReadEvent && (
          <div
            className="triangle-bottomleft"
            style={{ position: 'absolute', bottom: 0, left: 0 }}
          />
        )}
        {day.tachographReadEvent && (
          <div
            className="triangle-bottomright"
            style={{ position: 'absolute', bottom: 0, right: 0 }}
          />
        )}
      </FlexView>
    )
  },
)
